import type { ProvinceConfig } from "../_types";
import { COMPANY } from "@/lib/company";
import { LAWYERS } from "@/lib/lawyers";
import { getBranchesForProvince, NEARBY_PROVINCES_MAP } from "@/lib/branches-master";

const phuketBranches = getBranchesForProvince("phuket").map((b) => ({
  id: b.id,
  name: b.name,
  nameEn: b.nameEn,
  address: b.address,
  addressEn: b.address,
  locality: "Phuket",
  region: "Phuket",
  postalCode: b.postalCode,
  geo: b.geo ?? { lat: 7.8969, lng: 98.2956 },
  mapUrl: `https://maps.google.com/?q=${encodeURIComponent(b.address)}`,
  isPrimary: b.isPrimaryForProvince,
}));

export const PHUKET_CONFIG: ProvinceConfig = {
  slug: "phuket",
  nameTh: "ภูเก็ต",
  nameEn: "Phuket",
  iso: "TH-83",
  domain: "https://phuket.thainotary.co.th",
  geo: { lat: 7.8804, lng: 98.3923 },
  districtCount: 3,
  branches:
    phuketBranches.length > 0
      ? phuketBranches
      : [
          {
            id: "phuket-patong",
            name: "สำนักงานภูเก็ต (ป่าตอง)",
            nameEn: "Phuket Patong Office",
            address: "ถนนบางลา ตำบลป่าตอง อำเภอกะทู้ จังหวัดภูเก็ต 83150",
            addressEn: "Bangla Rd., Patong, Kathu, Phuket 83150",
            locality: "Phuket",
            region: "Phuket",
            postalCode: "83150",
            geo: { lat: 7.8969, lng: 98.2956 },
            mapUrl: "https://maps.google.com/?q=Patong+Phuket",
            isPrimary: true,
          },
        ],
  primaryBranchId: "phuket-patong",
  lawyers: LAWYERS.map((l) => ({ id: l.id })),

  districts: [
    {
      slug: "mueang",
      nameTh: "อำเภอเมืองภูเก็ต / ย่านเมืองเก่า",
      nameEn: "Mueang Phuket / Old Town",
      geo: { lat: 7.8804, lng: 98.3923 },
      landmarks: [
        "ถนนถลาง",
        "ย่านเมืองเก่าภูเก็ต (ชิโน-โปรตุกีส)",
        "สำนักทะเบียนอำเภอเมืองภูเก็ต",
        "ตลาดสด ตลาดใหญ่",
      ],
      intro:
        "อำเภอเมืองภูเก็ต — ใจกลางเกาะ ย่านเมืองเก่าสถาปัตยกรรมชิโน-โปรตุกีส ที่ตั้งศาลากลางจังหวัด สำนักงานที่ดิน ตม.ภูเก็ต และสำนักทะเบียนอำเภอเมือง เป็นพื้นที่ที่ลูกค้าต่างชาติใช้บริการ Notary Public, รับรองลายมือชื่อ, และจดทะเบียนสมรสไทย-ต่างชาติบ่อยที่สุดในจังหวัด ทีมเรามีสำนักงานสาขาในเมืองพร้อมรับเอกสาร walk-in และวิ่งงานราชการ (MFA, สถานกงสุล, ขนส่ง, ที่ดิน) แทนลูกค้าทุกวันทำการ ครอบคลุมทั้งย่านถนนถลาง ตลาดใหญ่ สะพานหิน และซอยตรอกใกล้ศูนย์ราชการ",
    },
    {
      slug: "patong",
      nameTh: "ป่าตอง",
      nameEn: "Patong",
      geo: { lat: 7.8969, lng: 98.2956 },
      landmarks: ["หาดป่าตอง", "ถนนบางลา", "จังซีลอน", "โรงพยาบาลกรุงเทพภูเก็ต"],
      intro:
        "ป่าตอง — ศูนย์กลางท่องเที่ยวและไนท์ไลฟ์ของภูเก็ต รวมโรงแรม 4-5 ดาวริมหาดและธุรกิจ F&B ระดับโลก ลูกค้าหลักของเราในย่านนี้คือนักท่องเที่ยวรัสเซีย-จีน-ยุโรปที่ต้องการเอกสารด่วนก่อนบินกลับ เจ้าของธุรกิจโรงแรม-ผับ-ร้านอาหารที่ต้องการ Work Permit และ BOI และนักลงทุนคอนโด/วิลล่าใกล้หาดที่ต้องเซ็นสัญญาซื้อ-ขายต่อหน้าทนาย เรามีสำนักงานหลักบนถนนราษฎร์อุทิศ 200 ปี ใกล้จังซีลอน เปิดทุกวัน 08:00–20:00 พร้อมบริการ Mobile Notary ถึงโรงแรมและโรงพยาบาลกรุงเทพภูเก็ตภายใน 60 นาที",
    },
    {
      slug: "kathu",
      nameTh: "กะทู้ / ล็อคกะทู้",
      nameEn: "Kathu / Loch Palm",
      geo: { lat: 7.9023, lng: 98.3334 },
      landmarks: ["สนามกอล์ฟล็อคปาล์ม", "Boat Avenue / Laguna", "บิ๊กซีกะทู้"],
      intro:
        "กะทู้-ล็อคปาล์ม-เชิงทะเล — ย่านที่อยู่อาศัยระดับ premium ของภูเก็ต ใกล้ป่าตองแต่เงียบสงบกว่า ครอบคลุมสนามกอล์ฟล็อคปาล์ม โครงการ Boat Avenue, Laguna Phuket, Cherngtalay และคอมมูนิตี้ Expat ครอบครัว ลูกค้าหลักของเราในย่านนี้คือนักลงทุนอสังหาฯ ที่ต้องการ Notary และทนายตรวจสัญญาซื้อ-ขายวิลล่ามูลค่า 10-80 ล้านบาท สัญญาเช่า 30 ปี เอกสาร BOI และ Power of Attorney ทีมงานบริการ Mobile Notary ถึงโครงการในกะทู้และเชิงทะเล พร้อมประสานสำนักงานที่ดินภูเก็ตและทนายฝ่ายผู้ขายให้ครบในวันเดียว",
    },
    {
      slug: "thalang",
      nameTh: "ถลาง / สนามบิน",
      nameEn: "Thalang / Airport",
      geo: { lat: 8.1132, lng: 98.3167 },
      landmarks: [
        "สนามบินนานาชาติภูเก็ต",
        "หาดไม้ขาว",
        "หาดในยาง",
        "Laguna Phuket",
      ],
      intro:
        "ถลาง-สนามบิน-ไม้ขาว-ในยาง — ประตูสู่ภูเก็ตและที่ตั้งโรงแรมระดับ 5 ดาวริมหาดเหนือ เช่น JW Marriott, Anantara, Trisara, Aleenta และ Laguna Phuket ครอบคลุม ตม.ภูเก็ตเทพกระษัตรี ซึ่งเป็นที่ทำ 90-day report, Re-entry และ Non-O ลูกค้าหลักของเราในย่านนี้คือนักท่องเที่ยวที่ต้องการ Express Notary และเอกสารด่วนก่อนบินกลับ เจ้าของวิลล่าริมหาดเหนือ Expat ที่อาศัยใน Laguna และนักธุรกิจที่ต้องเซ็นเอกสารต่อหน้าทนายระหว่าง transit ทีมเราให้บริการ Airport Pick-up รับ-ส่งเอกสารถึงเทอร์มินัลผู้โดยสารระหว่างประเทศ",
    },
    {
      slug: "rawai",
      nameTh: "ราไวย์ / ในหาน",
      nameEn: "Rawai / Nai Harn",
      geo: { lat: 7.7879, lng: 98.3254 },
      landmarks: ["หาดราไวย์", "หาดในหาน", "แหลมพรหมเทพ", "ท่าเรือราไวย์"],
      intro:
        "ราไวย์-ในหาน-แหลมพรหมเทพ — ย่านพักอาศัย Expat ระดับ premium และฮับ Digital Nomad ที่ใหญ่ที่สุดในภาคใต้ ลูกค้าหลักของเราในย่านนี้คือ Digital Nomad ที่ยื่น DTV Visa, Expat เกษียณที่ต้องต่อ Retirement Visa และ Non-O, คู่รักไทย-ต่างชาติที่เตรียม Affidavit + จดทะเบียนสมรส และเจ้าของวิลล่าที่ต้องเซ็นสัญญาเช่า 30 ปี ทีมเราเข้าถึงวิลล่าและ coworking space ในราไวย์-ในหาน-ในทอน ครอบคลุมโครงการริมหาดและบนเขา พร้อมประสาน ตม.ภูเก็ตให้ครบในแพ็กเกจเดียว",
    },
    {
      slug: "chalong",
      nameTh: "ฉลอง / มารีน่า",
      nameEn: "Chalong Bay / Marina",
      geo: { lat: 7.8214, lng: 98.3387 },
      landmarks: [
        "ท่าเรืออ่าวฉลอง",
        "Royal Phuket Marina",
        "Boat Lagoon",
        "วัดฉลอง",
      ],
      intro:
        "ฉลอง-อ่าวฉลอง-มารีน่า — ศูนย์กลางเรือยอร์ชและธุรกิจทางทะเลของภูเก็ต ครอบคลุม Royal Phuket Marina, Boat Lagoon, Ao Po Grand Marina และท่าเรืออ่าวฉลองที่เป็นจุดออก-เข้าน่านน้ำสากล ลูกค้าหลักของเราในย่านนี้คือเจ้าของเรือยอร์ชต่างชาติ กัปตัน-ลูกเรือต่างชาติ และบริษัท Yacht Charter ที่ต้องการ Notary สำหรับสัญญาเช่าเรือ Bareboat/Crewed, ใบอนุญาตเดินเรือ, ประกันภัยทางทะเล (P&I), Bill of Sale, Crew Visa และเอกสาร MARPOL/ISPS — เป็น niche service ที่ทีมเราเชี่ยวชาญที่สุดในภูเก็ต",
    },
  ],

  allDistricts: ["อำเภอเมืองภูเก็ต", "อำเภอกะทู้", "อำเภอถลาง"],

  landmarks: [
    "หาดป่าตอง",
    "ย่านเมืองเก่าภูเก็ต (ชิโน-โปรตุกีส)",
    "พระพุทธมิ่งมงคลเอกนาคคีรี (Big Buddha)",
    "แหลมพรหมเทพ",
    "สนามบินนานาชาติภูเก็ต",
    "จังซีลอน",
    "เซ็นทรัลภูเก็ต",
    "ถนนบางลา",
    "ท่าเรืออ่าวฉลอง",
    "Royal Phuket Marina",
    "Boat Lagoon",
    "หาดกะตะ",
    "หาดกะรน",
    "หาดในหาน",
    "หาดราไวย์",
    "หาดไม้ขาว",
    "หาดในยาง",
    "สนามกอล์ฟล็อคปาล์ม",
    "วัดฉลอง",
    "Laguna Phuket",
  ],

  businessZones: [
    "ป่าตอง",
    "ย่านเมืองเก่าภูเก็ต",
    "ราไวย์-ในหาน",
    "อ่าวฉลอง-มารีน่า",
    "ถลาง-สนามบิน",
    "Boat Avenue / Laguna",
    "กะทู้-ล็อคปาล์ม",
  ],

  universities: [
    "มหาวิทยาลัยสงขลานครินทร์ วิทยาเขตภูเก็ต (PSU Phuket)",
    "มหาวิทยาลัยราชภัฏภูเก็ต",
  ],

  hospitals: [
    "โรงพยาบาลกรุงเทพภูเก็ต (Bangkok Hospital Phuket)",
    "โรงพยาบาลวชิระภูเก็ต",
    "Mission Hospital Phuket",
    "Phuket International Hospital (Bangkok Dusit)",
    "Siriroj International Hospital",
  ],

  immigrationOffice:
    "สำนักงานตรวจคนเข้าเมืองจังหวัดภูเก็ต (Phuket Immigration) — ถนนเทพกระษัตรี ตำบลตลาดใหญ่ อำเภอเมืองภูเก็ต",

  nearbyProvinces: NEARBY_PROVINCES_MAP["phuket"] ?? ["พังงา", "กระบี่"],
  hours: COMPANY.hours,
  priceRange: "฿฿",
};